import React from 'react';

const WorksheetFilterPanel = ({
  filters,
  onChange,
  onReset,
  analyzers = [],
  isCollapsed,
  onToggleCollapse
}) => {
  const handleInputChange = (field, value) => {
    onChange({
      ...filters,
      [field]: value,
      pageNumber: 1 // reset to first page on filter change
    });
  };

  const handleDatePreset = (preset) => {
    const today = new Date();
    let fromDate = new Date();
    let toDate = new Date();

    if (preset === 'yesterday') {
      fromDate.setDate(today.getDate() - 1);
      toDate.setDate(today.getDate() - 1);
    } else if (preset === 'week') {
      fromDate.setDate(today.getDate() - 7);
    }

    const fmt = (d) => d.toISOString().split('T')[0];

    onChange({
      ...filters,
      fromDate: fmt(fromDate),
      toDate: fmt(toDate),
      pageNumber: 1
    });
  };

  return (
    <aside className={`ws-filter-panel ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="ws-filter-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>⚙️ Filter Workbench</span>
        </div>
        <button
          type="button"
          className="ws-btn ws-btn-outline ws-btn-sm"
          onClick={onToggleCollapse}
          title="Collapse Filter Panel"
        >
          ✕
        </button>
      </div>

      <div className="ws-filter-body">
        {/* Barcode / Search Input */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Quick Search / Barcode</label>
          <div className="ws-barcode-input-wrapper">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 5v14M8 5v14M12 5v14M17 5v14M21 5v14" />
            </svg>
            <input
              type="text"
              className="ws-filter-input"
              placeholder="Scan Barcode / Lab No..."
              value={filters.searchTerm || ''}
              onChange={(e) => handleInputChange('searchTerm', e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Date Presets */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Date Range</label>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-sm"
              style={{ flex: 1, padding: '3px 6px' }}
              onClick={() => handleDatePreset('today')}
            >
              Today
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-sm"
              style={{ flex: 1, padding: '3px 6px' }}
              onClick={() => handleDatePreset('yesterday')}
            >
              Yesterday
            </button>
            <button
              type="button"
              className="ws-btn ws-btn-outline ws-btn-sm"
              style={{ flex: 1, padding: '3px 6px' }}
              onClick={() => handleDatePreset('week')}
            >
              7 Days
            </button>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              type="date"
              className="ws-filter-input"
              style={{ width: '50%' }}
              value={filters.fromDate || ''}
              onChange={(e) => handleInputChange('fromDate', e.target.value)}
            />
            <input
              type="date"
              className="ws-filter-input"
              style={{ width: '50%' }}
              value={filters.toDate || ''}
              onChange={(e) => handleInputChange('toDate', e.target.value)}
            />
          </div>
        </div>

        {/* Section / SubCategory */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Laboratory Section</label>
          <select
            className="ws-filter-select"
            value={filters.subCategoryId || ''}
            onChange={(e) => handleInputChange('subCategoryId', e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">All Pathology Sections</option>
            <option value="1">Hematology</option>
            <option value="2">Biochemistry / Clinical Chemistry</option>
            <option value="3">Immunology / Immunoassay</option>
            <option value="4">Serology</option>
            <option value="5">Coagulation</option>
            <option value="6">Clinical Pathology / Urine</option>
          </select>
        </div>

        {/* Priority Filter */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Priority</label>
          <select
            className="ws-filter-select"
            value={filters.priority || ''}
            onChange={(e) => handleInputChange('priority', e.target.value || null)}
          >
            <option value="">All Priorities</option>
            <option value="STAT">⚡ STAT (Emergency)</option>
            <option value="Urgent">🔥 Urgent</option>
            <option value="Routine">Routine</option>
          </select>
        </div>

        {/* Status Filter */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Work Status</label>
          <select
            className="ws-filter-select"
            value={filters.status || ''}
            onChange={(e) => handleInputChange('status', e.target.value || null)}
          >
            <option value="">All Active Statuses</option>
            <option value="Pending">Pending (Not Started)</option>
            <option value="In Progress">In Progress (Testing)</option>
            <option value="Hold">On Hold</option>
            <option value="Completed">Completed</option>
            <option value="Repeat">Repeat Required</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {/* Sample Type Filter */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Sample Type</label>
          <select
            className="ws-filter-select"
            value={filters.sampleType || ''}
            onChange={(e) => handleInputChange('sampleType', e.target.value || null)}
          >
            <option value="">All Sample Types</option>
            <option value="EDTA">EDTA Whole Blood</option>
            <option value="Serum">Serum</option>
            <option value="Plasma">Plasma</option>
            <option value="Citrate">Citrate Plasma</option>
            <option value="Fluoride">Fluoride / Glucose Plasma</option>
            <option value="Urine">Urine</option>
          </select>
        </div>

        {/* Analyzer Filter */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Assigned Analyzer</label>
          <select
            className="ws-filter-select"
            value={filters.analyzerId || ''}
            onChange={(e) => handleInputChange('analyzerId', e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">All Analyzers</option>
            {analyzers.map((a) => (
              <option key={a.analyzerId} value={a.analyzerId}>
                {a.analyzerName} ({a.model})
              </option>
            ))}
          </select>
        </div>

        {/* Phase 6D: Machine Telemetry Bench Filter */}
        <div className="ws-filter-group">
          <label className="ws-filter-label">Analyzer Bench Run State</label>
          <select
            className="ws-filter-select"
            value={filters.machineFilter || 'ALL'}
            onChange={(e) => handleInputChange('machineFilter', e.target.value)}
          >
            <option value="ALL">All Specimens</option>
            <option value="MANUAL">Manual Only</option>
            <option value="ON_ANALYZER">🤖 On Analyzer</option>
            <option value="READY">✓ Machine Ready</option>
            <option value="REVIEW">⚠️ Validation Required</option>
            <option value="CRITICAL">🚨 Critical Panic Results</option>
          </select>
        </div>

        {/* TAT Risk Checkbox */}
        <div className="ws-filter-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            id="chk-tat-risk"
            checked={!!filters.tatRiskOnly}
            onChange={(e) => handleInputChange('tatRiskOnly', e.target.checked)}
          />
          <label htmlFor="chk-tat-risk" className="ws-filter-label" style={{ cursor: 'pointer' }}>
            Show TAT Risk Only (&lt; 30m / Overdue)
          </label>
        </div>

        {/* Reset Button */}
        <div style={{ marginTop: '10px' }}>
          <button
            type="button"
            className="ws-btn ws-btn-outline"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={onReset}
          >
            ↺ Reset All Filters
          </button>
        </div>
      </div>
    </aside>
  );
};

export default WorksheetFilterPanel;
